import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "table-container",
  id: "data-list"
}
const _hoisted_2 = {
  key: 0,
  class: "spinner-overlay"
}
const _hoisted_3 = { class: "ms-table table table-sm" }
const _hoisted_4 = ["file-row-index", "id"]
const _hoisted_5 = ["id"]
const _hoisted_6 = ["id"]
const _hoisted_7 = { class: "d-flex flex-wrap align-items-center" }
const _hoisted_8 = {
  key: 0,
  class: "subcategory-active NKT-P2 ms-2"
}
const _hoisted_9 = ["id"]
const _hoisted_10 = ["id"]
const _hoisted_11 = ["id", "data-order"]
const _hoisted_12 = ["id", "data-order"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]

import {
  DocumentationClient,
  SparePartsClient,
  SurveyClient,
  File,
  RelationType
} from '@/server/MakeSenseApp-backend'
import { useProjectStore } from '@/stores/ProjectStore'
import InlineConfirmationDialog from '@/components/dialogs/InlineConfirmationDialog.vue'
import moment from 'moment'
import { ref, PropType, computed, watch, onMounted, nextTick } from 'vue'
import { useDocumentationStore } from '@/stores/DocumentationStore'
import { SubCategoryValues } from '@/utilities/subCategoryValues'
import { RelationValues } from '@/utilities/relationsValues'
import '@/styles/ms-table-styles.scss'
import { requiredInject } from '@/utilities/requiredInject'
import { useUserStore } from '@/stores/UserStore'
import List from 'list.js'


export default /*@__PURE__*/_defineComponent({
  __name: 'FilesTable',
  props: {
  files: {
    type: Array as PropType<File[]>,
    default: () => []
  },
  isLoading: Boolean,
  showDelete: Boolean
},
  setup(__props) {

const props = __props

var showDeleteFile = ref<string | undefined>('')

const documentationClient = requiredInject<DocumentationClient>('DocumentationClient')
const surveyClient = requiredInject<SurveyClient>('SurveyClient')
const sparePartsClient = requiredInject<SparePartsClient>('SparePartsClient')

var projectStore = useProjectStore()
var documentationStore = useDocumentationStore()

var deletingFile = ref<File | undefined>(undefined)
var downloadingFile = ref<File | undefined>(undefined)

const userStore = useUserStore()
const files = computed(() => props.files)
const filesVersion = ref(0)

function getName(subcategory: string | undefined): string {
  if (!subcategory) return '-'

  if (subcategory === 'Marine survey data file') return subcategory

  if (subcategory === 'Inventory list') return subcategory

  if (subcategory === 'Cable shape file') return subcategory

  return SubCategoryValues.find((x) => x.value === subcategory)?.text ?? '-'
}

function getRelationDisplayName(relation: RelationType) {
  return RelationValues.find((x) => x.value === relation)?.text ?? '-'
}

function clickDeleteFile(file: File) {
  showDeleteFile.value = file.id
}

let documentationFileList: List | null = null

const sortFiles = () => {
  const options = {
    valueNames: [
      'name',
      'owner',
      'relations',
      { name: 'size', attr: 'data-order' },
      { name: 'last-modified', attr: 'data-order' },
      'sub-category'
    ]
  }

  documentationFileList = new List('data-list', options)

  if (documentationFileList.items.length > 0) {
    documentationFileList.sort('name', { order: 'asc' })
  }
}

// Watch for changes in `files` to handle async updates
onMounted(() => {
  watch(
    files,
    async (newFiles) => {
      filesVersion.value++
      if (newFiles.length > 0) {
        await nextTick() // ensure DOM updates are done
        sortFiles()
      }
    },
    { immediate: true }
  )
})

function cancel() {
  setTimeout(() => {
    showDeleteFile.value = undefined
  }, 1)
}

function deleteFile(file: File) {
  deletingFile.value = file

  if (file.relatedItem && file.subcategory === 'Marine survey data file') {
    surveyClient.deleteSurvey(projectStore.projectId, file.relatedItem)
  }

  if (file.relatedItem && file.subcategory === 'Inventory list') {
    sparePartsClient.deleteSparePart(file.relatedItem)
  }

  documentationClient
    .deleteFile(projectStore.projectId, file.id!)
    .then(() => {
      showDeleteFile.value = undefined
      documentationStore.reloadFiles()
      deletingFile.value = undefined
    })
    .catch((error) => {
      console.error(error)
      deletingFile.value = undefined
    })
}

const formatFileSize = (sizeInBytes: number): string => {
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024))
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const size = (sizeInBytes / Math.pow(1024, i)).toFixed(2)
  return `${size} ${sizes[i]}`
}

const formatTimestamp = (timestamp: Date | undefined): string => {
  if (!timestamp) return '-'
  return moment(timestamp).format('yyyy-MM-DD HH:mm')
}

const downloadFile = async (file: File) => {
  try {
    downloadingFile.value = file
    const response = await documentationClient.downloadFile(projectStore.projectId, file.id!)

    const blob = response.data
    const url = URL.createObjectURL(blob)

    const link: any = document.createElement('a')
    link.href = url
    link.download = file.name // Use the file's name

    link.click()

    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading file:', error)
  }

  downloadingFile.value = undefined
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[0] || (_cache[0] = [
          _createElementVNode("div", {
            class: "spinner-border",
            role: "status"
          }, [
            _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
          ], -1)
        ])))
      : _createCommentVNode("", true),
    _createElementVNode("table", _hoisted_3, [
      _cache[1] || (_cache[1] = _createElementVNode("thead", { class: "NKT-P1-medium sticky-header" }, [
        _createElementVNode("tr", null, [
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "name",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Name")
          ]),
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "sub-category",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Subcategory")
          ]),
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "owner",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Owner")
          ]),
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "relations",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Relations")
          ]),
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "size",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Size")
          ]),
          _createElementVNode("th", {
            class: "sort",
            "data-sort": "last-modified",
            scope: "col"
          }, [
            _createElementVNode("a", {
              href: "#",
              class: "text-body-secondary list-sort"
            }, "Last Modified")
          ]),
          _createElementVNode("th", {
            scope: "col",
            style: {"width":"80px"}
          })
        ])
      ], -1)),
      (!__props.isLoading)
        ? (_openBlock(), _createElementBlock("tbody", {
            class: "list",
            key: filesVersion.value
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
              return (_openBlock(), _createElementBlock("tr", {
                class: "ms-row",
                key: file.id,
                "file-row-index": index,
                id: `fileRow-${index}`
              }, [
                _createElementVNode("td", {
                  class: "name",
                  scope: "row",
                  id: `fileRow-${index}-name`
                }, _toDisplayString(file.name), 9, _hoisted_5),
                _createElementVNode("td", {
                  id: `fileRow-${index}-subcategory`,
                  class: "sub-category"
                }, [
                  _createElementVNode("div", _hoisted_7, [
                    _createTextVNode(_toDisplayString(getName(file.subcategory)) + " ", 1),
                    (
                  file.subcategory === 'Marine survey data file' ||
                  file.subcategory === 'Inventory list' ||
                  file.subcategory === 'Cable shape file'
                )
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, " Active "))
                      : _createCommentVNode("", true)
                  ])
                ], 8, _hoisted_6),
                _createElementVNode("td", {
                  id: `fileRow-${index}-owner`,
                  class: "owner"
                }, _toDisplayString(file.fileOwner?.name), 9, _hoisted_9),
                _createElementVNode("td", {
                  id: `fileRow-${index}-relationType`,
                  class: "relations"
                }, _toDisplayString(file.relationType?.map((x) => getRelationDisplayName(x)).join(', ')), 9, _hoisted_10),
                _createElementVNode("td", {
                  id: `fileRow-${index}-fileSize`,
                  class: "size",
                  "data-order": file.fileSize ?? 0
                }, _toDisplayString(formatFileSize(file.fileSize ?? 0)), 9, _hoisted_11),
                _createElementVNode("td", {
                  id: `fileRow-${index}-timestamp`,
                  class: "last-modified",
                  "data-order": file.timestamp?.getTime() ?? 0
                }, _toDisplayString(formatTimestamp(file.timestamp)), 9, _hoisted_12),
                _createElementVNode("td", null, [
                  (__props.showDelete && _unref(userStore).isOwnerOrHigher)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        onClick: () => clickDeleteFile(file),
                        class: _normalizeClass(["small-icon ms-icon", {
                'spinner-border spinner-border-sm': _unref(deletingFile) === file,
                'fe fe-trash-2 ': _unref(deletingFile) !== file
              }]),
                        style: {"position":"relative"}
                      }, [
                        _createVNode(InlineConfirmationDialog, {
                          show: _unref(deletingFile) !== file && _unref(showDeleteFile) === file.id,
                          onCancel: cancel,
                          onConfirm: () => deleteFile(file),
                          title: 'Are you sure you want to delete?',
                          "show-comment": false
                        }, null, 8, ["show", "onConfirm"])
                      ], 10, _hoisted_13))
                    : _createCommentVNode("", true),
                  _createElementVNode("a", {
                    onClick: ($event: any) => (downloadFile(file)),
                    target: "_blank"
                  }, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["small-icon ms-icon", {
                  'spinner-border spinner-border-sm': _unref(downloadingFile) === file,
                  'fe fe-download ': _unref(downloadingFile) !== file
                }])
                    }, null, 2)
                  ], 8, _hoisted_14)
                ])
              ], 8, _hoisted_4))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})